/** Region slug from topical_authority → chip label (e.g. hong_kong → Hong Kong). */
export const formatRegionLabelForChip = (region?: string | null): string => {
  const r = (region ?? '').trim().toLowerCase()
  if (!r || r === 'global') return 'Global'
  return r
    .split('_')
    .map((part) => (part.length ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
    .join(' ')
}

/** Label from Postgres `regions.display_label`, then city/country. */
export const formatPgRegionCityCountryLabel = (
  city?: string | null,
  country?: string | null,
  displayLabel?: string | null
): string => {
  const d = (displayLabel ?? '').trim()
  if (d) return d
  const c = (city ?? '').trim()
  const co = (country ?? '').trim()
  if (c && co) return `${c}, ${co}`
  if (c) return c
  if (co) return co
  return 'Unknown location'
}

/** Analyzing-bar line: `seo_pulse_report_region.domain_host` and `path_prefix`. */
export const formatSeoPulseRegionDomainPathDisplay = (
  domainHost?: string | null,
  pathPrefix?: string | null
): string => {
  const h = (domainHost ?? '').trim()
  const p = (pathPrefix ?? '').trim()
  const parts: string[] = []
  if (h) parts.push(h)
  if (p) parts.push(p)
  if (parts.length === 0) return '—'
  return parts.join(' · ')
}

/**
 * Accepts Firestore Timestamp instances, REST/JSON `{ seconds, nanoseconds }`,
 * and ISO strings. Avoids calling `toDate()` on partially deserialized objects
 * (e.g. missing internal millis), which throws.
 */
export const parseReportDateCreated = (value: unknown): Date => {
  const fallback = new Date()

  if (value == null) return fallback

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? fallback : parsed
  }

  if (typeof value !== 'object') return fallback

  const o = value as Record<string, unknown>

  const secondsRaw =
    typeof o.seconds === 'number'
      ? o.seconds
      : typeof o._seconds === 'number'
        ? o._seconds
        : null
  if (secondsRaw !== null) {
    const nanosRaw =
      (typeof o.nanoseconds === 'number' ? o.nanoseconds : null) ??
      (typeof o._nanoseconds === 'number' ? o._nanoseconds : null) ??
      0
    return new Date(secondsRaw * 1000 + nanosRaw / 1e6)
  }

  if (typeof o.toDate === 'function') {
    try {
      const out = (o.toDate as () => unknown)()
      if (out instanceof Date && !Number.isNaN(out.getTime())) return out
    } catch {
      // Serialized Timestamp-shaped object without backing millis
    }
  }

  return fallback
}

/** Parse comma-separated query param: empty / `all` → no filter. */
export const parseCommaSeparatedQueryParam = (raw: string | null): string[] => {
  if (raw == null || raw.trim() === '' || raw.trim() === 'all') return []
  return [...new Set(raw.split(',').map((s) => s.trim()).filter(Boolean))]
}

/** Serialize id list for URL; `null` → omit param (show all). */
export const serializeCommaSeparatedIds = (ids: readonly string[]): string | null => {
  const u = [...new Set(ids.map((x) => String(x).trim()).filter(Boolean))]
  if (u.length === 0) return null
  return u.join(',')
}

/** Parse `topic` query param: empty / `all` → no filter; comma-separated → doc ids. */
export const parseTopicQueryParam = parseCommaSeparatedQueryParam

/** Serialize topic keyword selection for URL `topic`; `null` → omit param (show all). */
export const serializeTopicKeywordIds = serializeCommaSeparatedIds

/** Parse `provider` query param (comma-separated engine ids). */
export const parseProviderQueryParam = parseCommaSeparatedQueryParam

/** Serialize selected engines for URL `provider`. */
export const serializeProviderIds = serializeCommaSeparatedIds

/** Query keys shared by the v1 report topbar and cross-page navigation. */
export const V1_REPORT_FILTER_QUERY_KEYS = [
  'region',
  'dateFrom',
  'dateTo',
  'provider',
  'intent',
  'topic',
] as const

export type V1ReportFilterQueryKey = (typeof V1_REPORT_FILTER_QUERY_KEYS)[number]

/** Copy active v1 topbar filters from the current URL (for sidebar / in-app links). */
export const pickV1ReportFilterSearchParams = (
  searchParams: Pick<URLSearchParams, 'get'>
): URLSearchParams => {
  const out = new URLSearchParams()
  for (const key of V1_REPORT_FILTER_QUERY_KEYS) {
    const value = searchParams.get(key)
    if (value == null) continue
    const trimmed = value.trim()
    if (!trimmed) continue
    out.set(key, trimmed)
  }
  return out
}

/** Append preserved v1 filter query string to a report section path. */
export const appendV1ReportFilterQueryToPath = (
  path: string,
  searchParams: Pick<URLSearchParams, 'get'>
): string => {
  const qs = pickV1ReportFilterSearchParams(searchParams).toString()
  if (!qs) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}${qs}`
}

/** Inclusive calendar-day cap for a selected v1 topbar date range. */
export const MAX_V1_REPORT_DATE_RANGE_DAY_COUNT = 14

/** Initial load window: last N inclusive calendar days ending at the latest crawl date. */
export const DEFAULT_V1_REPORT_DATE_RANGE_DAY_COUNT = 7

/** Content pages default: `citation_analysis_version.latest_crawl_date` ± this many days. */
export const CONTENT_CITATION_ANALYSIS_VERSION_WINDOW_DAYS = 3

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/

const inclusiveUtcDayCount = (from: string, to: string): number | null => {
  const start = new Date(`${from}T12:00:00Z`)
  const end = new Date(`${to}T12:00:00Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  return Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1
}

/** Inclusive UTC day count for a YYYY-MM-DD pair; null if either end is missing. */
export const getInclusiveUtcDayCount = (
  from: string | null,
  to: string | null
): number | null => {
  let start = from?.trim() || null
  let end = to?.trim() || null
  if (!start || !end) return null
  if (!ISO_DAY_RE.test(start) || !ISO_DAY_RE.test(end)) return null
  if (start > end) {
    const swap = start
    start = end
    end = swap
  }
  return inclusiveUtcDayCount(start, end)
}

/** Shift a YYYY-MM-DD by `deltaDays` using UTC calendar dates. */
export const addUtcCalendarDays = (isoDay: string, deltaDays: number): string | null => {
  const day = isoDay.trim()
  if (!ISO_DAY_RE.test(day)) return null
  const base = new Date(`${day}T12:00:00Z`)
  if (Number.isNaN(base.getTime())) return null
  base.setUTCDate(base.getUTCDate() + deltaDays)
  return base.toISOString().slice(0, 10)
}

/** Inclusive UTC calendar-day window centered on `isoDay` (YYYY-MM-DD). */
export const getDateRangeAroundIsoDay = (
  isoDay: string,
  windowDays = CONTENT_CITATION_ANALYSIS_VERSION_WINDOW_DAYS
): { from: string; to: string } | null => {
  const center = isoDay.trim()
  const from = addUtcCalendarDays(center, -windowDays)
  const to = addUtcCalendarDays(center, windowDays)
  if (!from || !to) return null
  return { from, to }
}

/**
 * Clamp a YYYY-MM-DD range to `maxDays` inclusive calendar days.
 * Empty both ends stays empty. A missing end is filled to the max window.
 * Oversized ranges keep `to` and move `from` forward.
 */
export const clampIsoDateRangeToMaxDays = (
  range: { from: string | null; to: string | null },
  maxDays = MAX_V1_REPORT_DATE_RANGE_DAY_COUNT
): { from: string; to: string } | null => {
  const span = Math.max(1, maxDays)
  let from = range.from?.trim() || null
  let to = range.to?.trim() || null
  if (!from && !to) return null
  if (from && !ISO_DAY_RE.test(from)) from = null
  if (to && !ISO_DAY_RE.test(to)) to = null
  if (!from && !to) return null
  if (from && to && from > to) {
    const swap = from
    from = to
    to = swap
  }
  if (from && !to) to = addUtcCalendarDays(from, span - 1)
  if (to && !from) from = addUtcCalendarDays(to, -(span - 1))
  if (!from || !to) return null
  const days = inclusiveUtcDayCount(from, to)
  if (days != null && days > span) {
    from = addUtcCalendarDays(to, -(span - 1))
  }
  if (!from || !to) return null
  return { from, to }
}

/**
 * Last `dayCount` inclusive calendar days ending at the latest available crawl date.
 * Defaults to {@link DEFAULT_V1_REPORT_DATE_RANGE_DAY_COUNT} (7). Pass
 * {@link MAX_V1_REPORT_DATE_RANGE_DAY_COUNT} for Clear (14).
 */
export const getLatestAvailableDateRange = (
  availableDates: readonly string[],
  dayCount = DEFAULT_V1_REPORT_DATE_RANGE_DAY_COUNT
): { from: string; to: string } | null => {
  const sorted = [...availableDates].map((d) => d.trim()).filter(Boolean).sort()
  if (sorted.length === 0) return null
  const to = sorted[sorted.length - 1]!
  return clampIsoDateRangeToMaxDays({ from: null, to }, dayCount)
}

/** Short label for v1 topbar date-range control (YYYY-MM-DD inputs). */
export const formatDateRangeChipLabel = (from: string | null, to: string | null): string => {
  const short = (s: string) => {
    const d = new Date(`${s}T12:00:00`)
    if (Number.isNaN(d.getTime())) return s
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  const f = from?.trim() || null
  const t = to?.trim() || null
  if (!f && !t) return 'All dates'
  if (f && t && f === t) return short(f)
  if (f && t) return `${short(f)} – ${short(t)}`
  if (f) return `From ${short(f)}`
  return `Until ${short(t!)}`
}

/** Single-date chip line with SERP/query count, aligned with v1 header pattern. */
export const formatDateChipFromReport = (
  dateCreated: unknown,
  count: number
): string => {
  const d = parseReportDateCreated(dateCreated)
  const safeCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0
  const line = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${line} (${safeCount})`
}
